import { useState } from "react";
import { Box, Group, Table, Text } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { money, timeLabel } from "./metrics";
import type { Bucket } from "./metrics";
import styles from "./dashboard.module.css";

export function DashboardChart({
  buckets,
  zone,
  title,
  monetary = false,
  color = "indigo",
}: {
  buckets: Bucket[];
  zone: string;
  title: string;
  monetary?: boolean;
  color?: string;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const { ref, width } = useElementSize();
  const chartWidth = Math.max(280, width || 960);
  const plotWidth = chartWidth - 70;
  const values = buckets.map((b) => (monetary ? Number(b.micro ?? 0n) : b.count));
  const max = Math.max(1, ...values);
  const valueLabel = (b: Bucket) =>
    monetary ? `${money(b.micro)} USDC` : b.count.toLocaleString();
  const active = selected === null ? null : buckets[selected];
  return (
    <Box ref={ref}>
      <Group justify="space-between" mb="xs" mih={24}>
        <Text size="xs" c="dimmed">
          {monetary ? "USDC received" : "Accounts created"} per bucket
        </Text>
        <Text size="xs" fw={600}>
          {active
            ? `${timeLabel(active.start, zone)} · ${valueLabel(active)}`
            : "Hover or focus a bar"}
        </Text>
      </Group>
      <svg viewBox={`0 0 ${chartWidth} 220`} role="img" aria-label={title} className={styles.chart}>
        {[0, 0.5, 1].map((ratio) => (
          <g key={ratio}>
            <line
              x1="55"
              x2={chartWidth - 10}
              y1={185 - ratio * 165}
              y2={185 - ratio * 165}
              stroke="var(--mantine-color-default-border)"
              strokeDasharray="3 5"
            />
            <text
              x="45"
              y={190 - ratio * 165}
              textAnchor="end"
              fill="var(--mantine-color-dimmed)"
              fontSize="12"
            >
              {monetary
                ? ((max * ratio) / 1_000_000).toLocaleString("en-US", {
                    notation: "compact",
                    maximumSignificantDigits: 3,
                  })
                : Math.ceil(max * ratio)}
            </text>
          </g>
        ))}
        {buckets.map((b, i) => {
          const slot = plotWidth / buckets.length;
          const gap = Math.min(2, slot * 0.15);
          const height = Math.max(values[i] > 0 ? 2 : 0, (values[i] / max) * 165);
          const label = `${timeLabel(b.start, zone)} to ${timeLabel(b.end, zone)}: ${valueLabel(b)}`;
          return (
            <g
              key={b.start}
              tabIndex={0}
              role="img"
              aria-label={label}
              onFocus={() => setSelected(i)}
              onBlur={() => setSelected(null)}
              onMouseEnter={() => setSelected(i)}
              onMouseLeave={() => setSelected(null)}
            >
              <title>{label}</title>
              <rect x={60 + i * slot} y="20" width={slot} height="165" fill="transparent" />
              <rect
                x={60 + i * slot + gap}
                y={185 - height}
                width={slot - 2 * gap}
                height={height}
                rx="3"
                fill={`var(--mantine-color-${color}-${selected === i ? 5 : 7})`}
              />
            </g>
          );
        })}
        {(chartWidth < 480
          ? [0, buckets.length - 1]
          : [0, Math.floor((buckets.length - 1) / 2), buckets.length - 1]
        )
          .filter((i, p, arr) => i >= 0 && arr.indexOf(i) === p)
          .map((i) => (
            <text
              key={i}
              x={i === buckets.length - 1 ? chartWidth - 10 : 60 + (i * plotWidth) / buckets.length}
              y="210"
              textAnchor={i === buckets.length - 1 ? "end" : "start"}
              fontSize="12"
              fill="var(--mantine-color-dimmed)"
            >
              {timeLabel(buckets[i].start, zone).slice(0, 12)}
            </text>
          ))}
      </svg>
      {values.every((v) => v === 0) && (
        <Text size="sm" c="dimmed" ta="center">
          No activity in this window.
        </Text>
      )}
      <details className={styles.dataTable}>
        <summary>View chart data</summary>
        <Table.ScrollContainer minWidth={440}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Bucket start ({zone})</Table.Th>
                <Table.Th>Value</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {buckets.map((b) => (
                <Table.Tr key={b.start}>
                  <Table.Td>{timeLabel(b.start, zone)}</Table.Td>
                  <Table.Td>{valueLabel(b)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </details>
    </Box>
  );
}
